import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { alpha, Box, IconButton } from '@mui/material';
import { memo, type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { tagCategoryId } from '@/enums';
import { useColorMode, useGetLineColor } from '@/hooks';
import { modals } from '@/modals';
import type { ILine, IMaterialObject, ITag } from '@/types';
import { formatLineLabel, isDraftLineTag } from '@/utils/lineHelpers';
import type { CellSizeMode } from './types';

const CHARACTER_COLOR = '#1580F9';
const LOCATION_COLOR = '#109A25';

interface CellTagStripProps {
    tags: ITag[];
    onRemoveTag?: (tagIds: number[]) => void;
    cellSizeMode?: CellSizeMode;
    locations?: IMaterialObject[];
    showLocations?: boolean;
    showCharacters?: boolean;
    showTags?: boolean;
    showLines?: boolean;
    /** Project lines — resolve code/color for attached line entity tags. */
    lines?: ILine[];
    /** Lines referenced by the entity's editor content (scene cards, #1830).
     *  Rendered as non-removable chips, deduped against attached line tags. */
    contentLines?: ILine[];
    tagFontSize?: string;
    parentContext?: { act: string; episode: string };
}

// Extract emoji from end of title: "My Tag 🎬" → { name: "My Tag", emoji: "🎬" }
const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})\s*$/u;

const parseTagTitle = (title: string) => {
    const match = title.match(emojiRegex);
    if (match) {
        const emoji = match[1];
        const name = title.slice(0, match.index).trim();
        return { name, emoji };
    }
    return { name: title, emoji: null };
};

interface TagChipProps {
    color: string;
    icon: ReactNode;
    label: string;
    tagId: number;
    onRemove?: (tagId: number) => void;
    isDarkMode: boolean;
    fontSize?: string;
}

const TagChip = ({ color, icon, label, tagId, onRemove, isDarkMode, fontSize = '10px' }: TagChipProps) => (
    <Box
        title={label}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '0 6px',
            height: fontSize === '10px' ? '18px' : '22px',
            // Same hue at different strengths: border ≈ tailwind 300, bg ≈ 50
            border: `1px solid ${alpha(color, 0.4)}`,
            borderRadius: '12px',
            backgroundColor: alpha(color, 0.08),
            maxWidth: '100%',
            flexShrink: 0,
        }}
    >
        {icon}
        <Box
            component="span"
            sx={{
                fontSize,
                lineHeight: 1,
                color,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {label}
        </Box>
        {onRemove && (
            <IconButton
                size="small"
                onClick={e => {
                    e.stopPropagation();
                    onRemove(tagId);
                }}
                sx={{
                    padding: 0,
                    color: theme => (isDarkMode ? theme.custom.c1 : theme.custom.c22),
                    '&:hover': { opacity: 0.7 },
                }}
            >
                <CloseIcon sx={{ fontSize: '10px' }} />
            </IconButton>
        )}
    </Box>
);

const ColorDot = ({ color }: { color: string }) => (
    <Box
        sx={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
        }}
    />
);

export const CellTagStrip = memo(
    ({
        tags,
        onRemoveTag,
        locations,
        showLocations = true,
        showCharacters = true,
        showTags = true,
        showLines = true,
        lines,
        contentLines,
        tagFontSize,
        parentContext,
    }: CellTagStripProps) => {
        const { t } = useTranslation();
        const getLineColor = useGetLineColor();
        const isDarkMode = useColorMode();

        const characterTags = useMemo(() => tags.filter(t => t.tag_category_id === tagCategoryId.characterTag), [tags]);
        const locationTags = useMemo(() => tags.filter(t => t.tag_category_id === tagCategoryId.locationTag), [tags]);
        const simpleTags = useMemo(
            () => tags.filter(t => t.tag_category_id === tagCategoryId.simpleTag).sort((a, b) => a.name.localeCompare(b.name)),
            [tags],
        );

        // Attached storyline entity tags, resolved to project lines for code/color.
        const attachedLines = useMemo(() => {
            const lineTags = tags.filter(t => t.tag_category_id === tagCategoryId.lineTag && !isDraftLineTag(t));
            return lineTags
                .map(tag => {
                    const line = lines?.find(l => l.entity_tag.id === tag.id);
                    return {
                        tag,
                        code: line?.code ?? tag.subject?.code ?? tag.name,
                        title: line?.title ?? tag.subject?.title ?? tag.name,
                        color: line?.color ?? tag.color,
                    };
                })
                .sort((a, b) => a.code.localeCompare(b.code));
        }, [tags, lines]);

        // Lines from editor content that aren't already attached as entity tags.
        const extraContentLines = useMemo(() => {
            if (!contentLines?.length) return [];
            const attachedTagIds = new Set(attachedLines.map(({ tag }) => tag.id));
            return contentLines
                .filter(line => line.code !== 'X' && !attachedTagIds.has(line.entity_tag.id))
                .sort((a, b) => a.code.localeCompare(b.code));
        }, [contentLines, attachedLines]);

        const resolvedLocationTags = useMemo(() => {
            if (!locations || locationTags.length === 0)
                return locationTags.map(t => ({
                    tag: t,
                    displayName: t.subject?.title ?? t.name,
                    parentTagId: null as number | null,
                }));

            const tagIds = new Set(locationTags.map(t => t.id));

            return locationTags
                .filter(tag => {
                    // Skip parent location tag if one of its sublocations is also attached
                    for (const loc of locations) {
                        if (loc.entity_tag.id === tag.id && loc.nested?.some(n => tagIds.has(n.entity_tag.id))) {
                            return false;
                        }
                    }
                    return true;
                })
                .map(tag => {
                    for (const loc of locations) {
                        if (loc.entity_tag.id === tag.id)
                            return {
                                tag,
                                displayName: loc.name,
                                parentTagId: null as number | null,
                            };
                        const sub = loc.nested?.find(n => n.entity_tag.id === tag.id);
                        if (sub)
                            return {
                                tag,
                                displayName: `${loc.name} · ${sub.name}`,
                                parentTagId: loc.entity_tag.id,
                            };
                    }
                    return {
                        tag,
                        displayName: tag.subject?.title ?? tag.name,
                        parentTagId: null as number | null,
                    };
                });
        }, [locationTags, locations]);

        const handleLineRemoveClick = useCallback(
            (tagId: number, code: string) => {
                if (!onRemoveTag) return;
                modals.confirm({
                    title: t('lines.delete-title'),
                    message: t('lines.delete-from-scene', {
                        code,
                        act: parentContext?.act ?? '',
                        episode: parentContext?.episode ?? '',
                    }),
                    confirmLabel: t('lines.delete-button', { code }),
                    cancelLabel: t('lines.close'),
                    onConfirm: () => onRemoveTag([tagId]),
                });
            },
            [onRemoveTag, parentContext, t],
        );

        const hasVisibleTags =
            (showLocations && resolvedLocationTags.length > 0) ||
            (showCharacters && characterTags.length > 0) ||
            (showTags && simpleTags.length > 0) ||
            (showLines && (attachedLines.length > 0 || extraContentLines.length > 0));

        if (!hasVisibleTags) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    marginTop: '2px',
                    flexShrink: 0,
                }}
                onClick={e => e.stopPropagation()}
            >
                {showCharacters &&
                    characterTags.map(tag => (
                        <TagChip
                            key={tag.id}
                            color={CHARACTER_COLOR}
                            icon={
                                <PersonOutlinedIcon
                                    sx={{
                                        fontSize: '10px',
                                        color: CHARACTER_COLOR,
                                        flexShrink: 0,
                                    }}
                                />
                            }
                            label={tag.subject?.title ?? tag.name}
                            tagId={tag.id}
                            onRemove={onRemoveTag ? () => onRemoveTag([tag.id]) : undefined}
                            isDarkMode={isDarkMode}
                            fontSize={tagFontSize}
                        />
                    ))}

                {showLocations &&
                    resolvedLocationTags.map(({ tag, displayName, parentTagId }) => (
                        <TagChip
                            key={tag.id}
                            color={LOCATION_COLOR}
                            icon={
                                <LocationOnOutlinedIcon
                                    sx={{
                                        fontSize: '10px',
                                        color: LOCATION_COLOR,
                                        flexShrink: 0,
                                    }}
                                />
                            }
                            label={displayName}
                            tagId={tag.id}
                            onRemove={onRemoveTag ? () => onRemoveTag(parentTagId ? [tag.id, parentTagId] : [tag.id]) : undefined}
                            isDarkMode={isDarkMode}
                            fontSize={tagFontSize}
                        />
                    ))}

                {showTags &&
                    simpleTags.map(tag => {
                        const color = getLineColor(tag.color);
                        const { name, emoji } = parseTagTitle(tag.name);
                        return (
                            <TagChip
                                key={tag.id}
                                color={color}
                                icon={<ColorDot color={color} />}
                                label={emoji ? `${name} · ${emoji}` : name}
                                tagId={tag.id}
                                onRemove={onRemoveTag ? () => onRemoveTag([tag.id]) : undefined}
                                isDarkMode={isDarkMode}
                                fontSize={tagFontSize}
                            />
                        );
                    })}

                {showLines &&
                    attachedLines.map(({ tag, code, title, color }) => {
                        const resolvedColor = getLineColor(color);
                        return (
                            <TagChip
                                key={`line-${tag.id}`}
                                color={resolvedColor}
                                icon={<ColorDot color={resolvedColor} />}
                                label={formatLineLabel({ code, title }, t('lines.line-prefix'))}
                                tagId={tag.id}
                                onRemove={onRemoveTag ? () => handleLineRemoveClick(tag.id, code) : undefined}
                                isDarkMode={isDarkMode}
                                fontSize={tagFontSize}
                            />
                        );
                    })}

                {showLines &&
                    extraContentLines.map(line => {
                        const resolvedColor = getLineColor(line.color);
                        return (
                            <TagChip
                                key={`content-line-${line.id}`}
                                color={resolvedColor}
                                icon={<ColorDot color={resolvedColor} />}
                                label={formatLineLabel(line, t('lines.line-prefix'))}
                                tagId={line.id}
                                isDarkMode={isDarkMode}
                                fontSize={tagFontSize}
                            />
                        );
                    })}
            </Box>
        );
    },
);

CellTagStrip.displayName = 'CellTagStrip';
